import dayjs from "dayjs";

import { fullMonthAndYear, shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { HeatmapCell } from "app/components/React/Table/cells";
import { DefaultCellHeader, DefaultCellHeaderRightAlignNoElipsis } from "app/components/React/Table/headerCells";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { fetchWithFullResponseInterceptorHandlers } from "app/components/widget/widget-fetchers/DefaultDataFetcher";
import { AppRetentionApiService } from "app/pages/app-intelligence/app-analysis/pages/Retention/services/AppRetentionApiService";
import { transformHistoricalData } from "app/pages/app-intelligence/app-analysis/pages/Retention/utilities/transformHistoricalData";
import DurationService from "app/services/DurationService";

import { TableTemplateV2 } from "../dashboard/TableTemplate-v2";

import { TableWidget } from "./TableWidget";

export class AppRetentionDashboardTableWidget extends TableWidget {
    public isPptSupported = () => {
        return false;
    };

    private tableDurationValues: {
        from: string;
        to: string;
    } = AppRetentionApiService.getDurationValuesForTableRequest("AppRetentionFront");
    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetMetadataType() {
        return "AppRetentionDashboardTable";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    getViewData() {
        super.getViewData();
        const { from, to } = this.tableDurationValues;
        const diff = DurationService.diffByUnit(from, to, "months");
        this.viewData.customSubtitle = this._i18nFilter("app.analysis.retention.historical.subtitle.date", {
            numOfMonths: diff + 1,
        });
        return this.viewData;
    }

    private dataFetcherFactory = () => {
        return {
            create: (widget) => {
                return {
                    fetch: () => {
                        const apiParams = {
                            ...widget.apiParams,
                            ...this.tableDurationValues,
                        };
                        return AppRetentionApiService.getTableData(apiParams)
                            .then(...fetchWithFullResponseInterceptorHandlers)
                            .then((rawData: any) => rawData);
                    },
                };
            },
        };
    };

    constructor() {
        super();
    }

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    get templateUrl() {
        return `/app/components/widget/widget-templates/table.html`;
    }

    get templateComponent() {
        return TableTemplateV2;
    }

    protected validateData(data) {
        return Object.values(data).some((val) => !!val);
    }

    public getProUrl(rowParams) {
        if (rowParams) {
            return super.getProUrl(rowParams);
        }
        const queryParams = { ...this._params };
        const apiParams = Object.assign(this._getProUrlParams(), this._metricConfig.stateParams, rowParams);
        // we set the duration to 1 month so that it always links to the current month.
        // This is because the historical retention table is not a function of the duration
        // selected but rather always displays up to 12 months back from the current date.
        apiParams.duration = "1m";
        return this._swNavigator.href(this._metricConfig.state, {
            ...apiParams,
        });
    }

    private getColumns = (singleDataObject) => {
        const getHeatmapColumn = ({ field, displayName }: { field: string; displayName: string }) => {
            return {
                field,
                displayName,
                cellClass: "appretention-dashboard-table-cell-heatmap",
                headerCellClass: "appretention-dashboard-table-header-cell",
                minWidth: 56,
                hideZeroValue: true,
                disableHeaderCellHover: true,
                visible: true,
                sortable: false,
                isResizable: false,
                headerComponent: DefaultCellHeaderRightAlignNoElipsis,
                cellComponent: ({ value, tableMetadata, hideZeroValue }) => {
                    const cellProps = {
                        value,
                        hideZeroValue: hideZeroValue,
                        tableMetadata: tableMetadata,
                    } as ITableCellProps;
                    return (
                        <HeatmapCell
                            {...cellProps}
                            backgroundColor={colorsPalettes.blue[400]}
                            useOpacity={true}
                            opacityPrecentage={0}
                            shouldBrightText={true}
                        />
                    );
                },
            };
        };
        return [
            {
                fixed: true,
                field: "date",
                displayName: this._i18nFilter("app.analysis.retention.historical.table.column.month"),
                cellClass: "appretention-dashboard-table-cell-date",
                cellComponent: ({ value }) => {
                    // TODO: add comment explaining why we subtract one month for the column value
                    const valueToDisplay = dayjs(value, shortMonthAndShortYear)
                        .subtract(1, "months")
                        .format(fullMonthAndYear);
                    return <div>{valueToDisplay}</div>;
                },
                disableHeaderCellHover: true,
                width: 136,
                headerComponent: DefaultCellHeader,
                sortable: false,
                visible: true,
                isResizable: false,
            },
            ...Object.keys(singleDataObject).map((key) =>
                getHeatmapColumn({
                    field: key,
                    displayName: this._i18nFilter("app.analysis.retention.historical.table.column.day", {
                        day: key.substring(1),
                    }),
                }),
            ),
        ];
    };

    public callbackOnGetData(response: any): void {
        const mainAppKey = this._widgetConfig.properties.key[0].id;
        const selectedAppData = response.Data[mainAppKey];
        this.metadata.columns = this.getColumns(Object.values(selectedAppData)[0]);
        const transformedData = transformHistoricalData(selectedAppData);
        super.callbackOnGetData(transformedData);
    }
}
AppRetentionDashboardTableWidget.register();
